/**
 * LE PAYS D'UNE FACTURE — un code ISO à deux lettres (« BE »), choisi dans une liste.
 *
 * ⚠️ C'EST UN CODE, PAS UN NOM, ET C'EST CE QUE LA FACTURE ATTEND DÉJÀ. Le générateur de
 * PDF qui sert les factures du paiement (apix, `genereateGayakoaPDF.js`) cherche
 * `countries[bill.country]` dans `countries-list` pour imprimer le nom ; le back office
 * saisit ses factures sous « Pays (Code) ». Le champ libre de la V1, repris d'abord ici,
 * produisait « Suisse », « SUISSE », « Suiss », « La Belgique » ou « johnny » : une
 * colonne où le même pays s'écrivait de dix façons, et que rien ne pouvait relire.
 *
 * ⚠️ LA LISTE EST CELLE DE `countries-list`, CLÉ POUR CLÉ (250 codes, Kosovo « XK »
 * compris). Un code qu'elle ne connaît pas sortirait du PDF tel quel, en deux lettres :
 * on n'en propose donc aucun autre.
 *
 * ⚠️ LES NOMS VIENNENT D'`Intl`, DANS LA LANGUE LUE. Une table de noms à nous aurait été
 * française sur le site anglais ; `Intl` les connaît tous, dans toutes les langues.
 *
 * Ce fichier est PUR : la reprise d'une ancienne saisie libre se teste sans écran
 * (voir `billCountries.test.ts`).
 */

// prettier-ignore
export const BILL_COUNTRIES: ReadonlyArray<string> = [
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "XK", "YE", "YT", "ZA", "ZM", "ZW",
];

const KNOWN = new Set(BILL_COUNTRIES);

/**
 * ⚠️ LA FRANCE, PUIS SES VOISINS, PUIS LE RESTE — l'ordre du champ téléphone
 * (`Champ.tsx`). Sans lui, un salon français ouvre sa liste sur l'Afghanistan.
 */
export const FIRST_BILL_COUNTRIES: ReadonlyArray<string> = ["FR", "BE", "CH", "LU"];

/** Le lecteur de noms d'une langue, ou rien si l'environnement refuse l'étiquette. */
function namer(locale: string): Intl.DisplayNames | null {
  try {
    /* Le français derrière : une langue du site qu'`Intl` ne sait pas nommer (le chinois
       est rangé sous « CN », un code de pays) retombe sur lui plutôt que sur la langue
       du navigateur. */
    return new Intl.DisplayNames([locale, "fr"], { type: "region" });
  } catch {
    return null;
  }
}

/** « Belgique » pour « BE », dans la langue lue ; le code lui-même faute de mieux. */
export function countryName(code: string, locale: string): string {
  return namer(locale)?.of(code) ?? code;
}

export type CountryOption = { code: string; name: string };

const OPTIONS = new Map<
  string,
  { first: ReadonlyArray<CountryOption>; others: ReadonlyArray<CountryOption> }
>();

/**
 * LA LISTE DU SÉLECTEUR, DANS LA LANGUE LUE : les voisins d'abord, puis tous les autres
 * par ordre alphabétique de CETTE langue (« Allemagne » en tête en français, « Austria »
 * en anglais).
 *
 * Dressée une fois par langue : l'écran se redessine à chaque touche frappée dans le
 * formulaire, et 250 noms triés à chaque fois n'apportent rien.
 */
export function countryOptions(locale: string): {
  first: ReadonlyArray<CountryOption>;
  others: ReadonlyArray<CountryOption>;
} {
  const cached = OPTIONS.get(locale);
  if (cached) return cached;
  const say = namer(locale);
  const option = (code: string) => ({ code, name: say?.of(code) ?? code });
  let collator: Intl.Collator;
  try {
    collator = new Intl.Collator([locale, "fr"]);
  } catch {
    collator = new Intl.Collator("fr");
  }
  const built = {
    first: FIRST_BILL_COUNTRIES.map(option),
    others: BILL_COUNTRIES.filter((c) => !FIRST_BILL_COUNTRIES.includes(c))
      .map(option)
      .sort((a, b) => collator.compare(a.name, b.name)),
  };
  OPTIONS.set(locale, built);
  return built;
}

/* « Côte d’Ivoire » et « COTE D'IVOIRE » se ressemblent assez pour être le même pays :
   sans accents, sans casse, la ponctuation ramenée à des espaces. */
const flatten = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z]+/g, " ")
    .trim();

/* Les langues des sites qu'on sert : une ancienne saisie libre est écrite dans l'une
   d'elles (« Suisse », « Italia », « Spain », « Allemagne »…). */
const NAME_LANGUAGES = ["fr", "en", "it", "es", "de", "pt", "nl"];

let byName: Map<string, string> | null = null;

function codeByName(): Map<string, string> {
  if (byName) return byName;
  byName = new Map();
  for (const language of NAME_LANGUAGES) {
    const say = namer(language);
    if (!say) continue;
    for (const code of BILL_COUNTRIES) {
      const name = flatten(say.of(code) ?? "");
      if (name !== "" && !byName.has(name)) byName.set(name, code);
    }
  }
  return byName;
}

/**
 * LE CODE D'UN PAYS DÉJÀ ÉCRIT SUR UNE FACTURE, ou rien.
 *
 * ⚠️ LES FACTURES PAYÉES AVANT CE SÉLECTEUR PORTENT UN NOM LIBRE, et la dernière revient
 * pré-remplir le formulaire (`readBill`). Un `<select>` ne sait pas montrer une valeur
 * qui n'est pas l'une de ses options : il afficherait « Choisissez » pendant que l'état
 * garderait « Suisse », et la facture partirait avec le nom que l'écran disait vide.
 * On ramène donc le nom à son code quand on le reconnaît (« SUISSE » → « CH »), et on
 * l'écarte sinon (« johnny », « QUEBEC ») : le payeur rechoisit, le champ étant marqué
 * s'il est exigé.
 */
export function countryCode(raw: string | undefined): string | undefined {
  const text = (raw ?? "").trim();
  if (text === "") return undefined;
  const upper = text.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper)) return KNOWN.has(upper) ? upper : undefined;
  return codeByName().get(flatten(text));
}
